// src/components/ThreadList.js
import React, { useEffect, useState } from 'react';
import supabase from '../supabase';

const ThreadList = ({ user, currentThread, setCurrentThread }) => {
  const [threads, setThreads] = useState([]);
  const [newThreadName, setNewThreadName] = useState('');

  const fetchThreads = async (userId) => {
    const { data, error } = await supabase
      .from('threads')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching threads:', error.message);
      alert(`Error fetching threads: ${error.message}`);
      return;
    }
    setThreads(data);
  };

  const handleCreateThread = async () => {
    if (!newThreadName.trim()) return;

    try {
      const { data, error } = await supabase
        .from('threads')
        .insert([{ user_id: user.id, name: newThreadName.trim() }])
        .select('*')
        .single();

      if (error) {
        console.error('Error creating thread:', error.message);
        alert(`Error creating thread: ${error.message}`);
        return;
      }

      setThreads((prev) => [data, ...prev]);
      setNewThreadName('');
      setCurrentThread(data);
    } catch (err) {
      console.error('Unexpected error during thread creation:', err);
      alert('An unexpected error occurred while creating the thread.');
    }
  };

  const handleDeleteThread = async (threadId) => {
    const { error } = await supabase
      .from('threads')
      .delete()
      .eq('id', threadId);

    if (error) {
      console.error('Error deleting thread:', error.message);
      alert(`Error deleting thread: ${error.message}`);
      return;
    }

    const updatedThreads = threads.filter(thread => thread.id !== threadId);
    setThreads(updatedThreads);

    if (currentThread && currentThread.id === threadId) {
      setCurrentThread(updatedThreads.length > 0 ? updatedThreads[0] : null);
    }
  };

  const handleSelectThread = (thread) => {
    if (!thread.id || !thread.name) {
      alert('Selected thread is invalid.');
      return;
    }
    setCurrentThread(thread);
  };

  useEffect(() => {
    if (user) {
      fetchThreads(user.id);
    }
  }, [user]);

  useEffect(() => {
    if (threads.length > 0 && !currentThread) {
      setCurrentThread(threads[0]);
    }
  }, [threads, currentThread, setCurrentThread]);

  return (
    <div className="p-4 h-full flex flex-col bg-gray-900 text-gray-200 border-r border-gray-800">
      {/* New Thread Input */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold mb-2">Add Thread</h2>
        <div className="flex">
          <input
            type="text"
            placeholder="Thread name..."
            value={newThreadName}
            onChange={(e) => setNewThreadName(e.target.value)}
            className="flex-1 p-2 border border-gray-700 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleCreateThread}
            className="bg-blue-900 text-white px-4 py-2 rounded-r-md hover:bg-blue-800 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Create
          </button>
        </div>
      </div>

      {/* Existing Threads */}
      <h3 className="text-lg font-semibold mb-2">Your Threads</h3>
      <ul className="space-y-2 flex-1 overflow-y-auto">
        {threads.map(thread => {
          const isSelected = thread.id === (currentThread?.id ?? null);
          return (
            <li
              key={thread.id}
              onClick={() => handleSelectThread(thread)}
              className={`p-2 rounded-md cursor-pointer flex justify-between items-center 
                ${isSelected ? 'bg-blue-900' : 'bg-gray-900'}
                hover:bg-blue-950 transition-colors`}
            >
              <span className="truncate">{thread.name}</span>
              <button
                className="ml-2 text-red-400 hover:text-red-300"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteThread(thread.id);
                }}
              >
                &times;
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ThreadList;
